import { useQuery, useQueryClient } from '@tanstack/react-query';
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import axios from 'axios';
import FetchErrorModal from 'src/components/FetchErrorModal';
import { CustomOption, MenuList } from 'src/components/ReactSelect';
import { stripDiacritics } from '../../../helpers/diacritics';
import { withVendorURL } from '../../../helpers/service';
import { ITradeMarkProps } from './types';

const _debounce = () => {
  let timer: null | NodeJS.Timeout = null;
  return (fc: any, ms = 500) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      if (fc?.call) fc();
    }, ms);
  };
};
const debounce = _debounce();

const searchThreshold = 2; // Minimum characters to trigger search

type TradeMarkOption = { value: string; label: string; noAccent?: string };

const normalizeTradeMarkValue = (raw: unknown): TradeMarkOption[] => {
  if (raw == null || raw === '') return [];

  const toOption = (item: unknown): TradeMarkOption => {
    if (item && typeof item === 'object' && 'value' in item) {
      const option = item as { value: string | number; label?: string };
      const value = String(option.value);
      return { value, label: option.label ?? value };
    }

    const value = String(item);
    return { value, label: value };
  };

  if (Array.isArray(raw)) return raw.map(toOption);
  return [toOption(raw)];
};

const optionValuesMatch = (left: string | number, right: string | number) => String(left) === String(right);

const TradeMarksField = forwardRef<any, ITradeMarkProps>((props, ref) => {
  const {
    fetchFromMCAPI = true,
    searchFromMCAPI = false,
    queryKey,
    customChangeValue = () => ({ label: '', value: '' }),
    onQueryChange,
    options,
    error,
    isDisabled,
    value,
    defaultValue,
    isMulti = true,
    ...selectProps
  } = props;

  const [internalSearchQuery, setInternalSearchQuery] = useState('');
  const [localSelectedValues, setLocalSelectedValues] = useState<TradeMarkOption[]>([]);

  const queryClient = useQueryClient();

  const normalizedValueItems = useMemo(() => normalizeTradeMarkValue(defaultValue ?? value), [defaultValue, value]);

  const selectedBrandIds = useMemo(() => normalizedValueItems.map((item) => item.value).filter(Boolean), [normalizedValueItems]);

  const getSelectedBrands = useQuery<any, any>({
    queryKey: ['selected-brands', queryKey, selectedBrandIds],
    queryFn: () => {
      return axios
        .post(withVendorURL('brand/brands-by-id-list'), {
          brandIds: selectedBrandIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id)),
        })
        .then((res) => res.data);
    },
    enabled: fetchFromMCAPI && selectedBrandIds.length > 0,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 5,
  });

  const getTradeMarks = useQuery<any, any>({
    queryKey: ['trade-marks', queryKey],
    queryFn: () => {
      return axios
        .get(withVendorURL(searchFromMCAPI ? 'brand/by-vendor' : 'brand/list'), {
          params: { title: searchFromMCAPI ? internalSearchQuery : undefined },
        })
        .then((res) => res.data);
    },
    enabled: fetchFromMCAPI && !searchFromMCAPI,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const handleInputChange = (inputValue: string) => {
    setInternalSearchQuery(inputValue.trim());
    if (inputValue.length >= searchThreshold && searchFromMCAPI) {
      debounce(() => {
        getTradeMarks.refetch();
      }, 500);
    }
  };

  const handleInternalChange = (selectedOptions: any, actionMeta: any) => {
    setLocalSelectedValues(Array.isArray(selectedOptions) ? selectedOptions : [selectedOptions].filter(Boolean));

    if (props.onChange) {
      props.onChange(selectedOptions, actionMeta);
    }
  };

  useEffect(() => {
    if (internalSearchQuery.length < searchThreshold && searchFromMCAPI) {
      queryClient.cancelQueries({ queryKey: ['trade-marks', queryKey] });
      queryClient.setQueryData(['trade-marks', queryKey], []);
    }
  }, [internalSearchQuery, searchFromMCAPI, queryClient, queryKey]);

  // Dışarıdan gelen value/defaultValue değişince (ör. filtre temizleme) local state'i senkron tut;
  // aksi halde react-select eski seçimi göstermeye devam eder.
  useEffect(() => {
    setLocalSelectedValues(normalizedValueItems);
  }, [normalizedValueItems]);

  const tradeMarksOptions = useMemo(() => {
    let returnedOptions: TradeMarkOption[] = [];

    if (!fetchFromMCAPI) return options;

    if (getTradeMarks.isSuccess) {
      returnedOptions = getTradeMarks.data.map((tradeMark: any) => ({
        value: String(tradeMark.id),
        label: tradeMark.title,
        noAccent: stripDiacritics(tradeMark.title).toLowerCase(),
      }));
    }

    // keepPreviousData cache'i boş seçimde eski markayı option listesine eklemesin.
    if (getSelectedBrands.isSuccess && getSelectedBrands.data && selectedBrandIds.length > 0) {
      const selectedOptions = getSelectedBrands.data.map((brand: any) => ({
        value: String(brand.id),
        label: brand.title ?? brand.name ?? String(brand.id),
        noAccent: stripDiacritics(brand.title ?? brand.name ?? '').toLowerCase(),
      }));

      selectedOptions.forEach((selected: TradeMarkOption) => {
        if (!returnedOptions.some((option) => optionValuesMatch(option.value, selected.value))) {
          returnedOptions.push(selected);
        }
      });
    }

    localSelectedValues.forEach((selected) => {
      if (!returnedOptions.some((option) => optionValuesMatch(option.value, selected.value))) {
        returnedOptions.push(selected);
      }
    });

    return returnedOptions;
  }, [fetchFromMCAPI, getTradeMarks.isSuccess, getTradeMarks.data, getSelectedBrands.isSuccess, getSelectedBrands.data, options, localSelectedValues]);

  const selectedValues = useMemo(() => {
    const resolveFromOptions = (items: TradeMarkOption[]) => items.map((item) => tradeMarksOptions.find((option) => optionValuesMatch(option.value, item.value)) ?? item);

    if (!fetchFromMCAPI) {
      return isMulti ? normalizedValueItems : normalizedValueItems[0];
    }

    const hasResolvedOptions = getTradeMarks.isSuccess || getSelectedBrands.isSuccess;
    // Prop ile kontrol ediliyorsa (null dahil) localSelectedValues'a düşme; temizleme böyle çalışır.
    const hasControlledValue = value !== undefined || defaultValue !== undefined;
    const sourceItems =
      normalizedValueItems.length > 0 || hasControlledValue ? normalizedValueItems : localSelectedValues;

    if (!hasResolvedOptions) {
      if (sourceItems.length === 0) {
        return isMulti ? null : undefined;
      }
      return isMulti ? sourceItems : sourceItems[0];
    }

    if (isMulti) {
      const resolved = resolveFromOptions(sourceItems);
      // Multi-select boşken undefined yerine null; react-select controlled modda böyle bekler.
      return resolved.length > 0 ? resolved : null;
    }

    const singleValue = normalizedValueItems[0] ?? localSelectedValues[0];
    if (!singleValue) return { value: '', label: '' };

    return tradeMarksOptions.find((option) => optionValuesMatch(option.value, singleValue.value)) ?? singleValue;
  }, [fetchFromMCAPI, getTradeMarks.isSuccess, getSelectedBrands.isSuccess, tradeMarksOptions, normalizedValueItems, localSelectedValues, isMulti, value, defaultValue]);

  useMemo(() => {
    if (!isMulti && (getTradeMarks.isSuccess || getSelectedBrands.isSuccess)) {
      const singleValue = normalizeTradeMarkValue(value)[0];
      customChangeValue((singleValue && tradeMarksOptions?.find((option) => optionValuesMatch(option.value, singleValue.value))) ?? { value: '', label: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTradeMarks.isSuccess, getSelectedBrands.isSuccess, tradeMarksOptions]);

  const stableOnQueryChange = useCallback(onQueryChange ?? (() => {}), []);

  useEffect(() => {
    if (stableOnQueryChange && (getTradeMarks.isLoading || getTradeMarks.isSuccess || getTradeMarks.isError)) {
      stableOnQueryChange(getTradeMarks);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTradeMarks.isLoading, getTradeMarks.isSuccess, getTradeMarks.isError, stableOnQueryChange]);

  const disabled = useMemo(() => {
    if (searchFromMCAPI) {
      return false;
    }
    return (getTradeMarks.isLoading && getTradeMarks.isFetching) || getTradeMarks.isError || isDisabled;
  }, [searchFromMCAPI, getTradeMarks.isLoading, getTradeMarks.isFetching, getTradeMarks.isError, isDisabled]);

  const loading = useMemo(() => {
    if (searchFromMCAPI) {
      return getTradeMarks.isFetching || getSelectedBrands.isFetching;
    }
    return (getTradeMarks.isLoading && getTradeMarks.isFetching) || getSelectedBrands.isFetching;
  }, [searchFromMCAPI, getTradeMarks.isFetching, getTradeMarks.isLoading, getSelectedBrands.isFetching]);

  const optionsMessage = useMemo(() => {
    if (searchFromMCAPI && internalSearchQuery.length < searchThreshold) {
      return `Lütfen ${searchThreshold} karakterden fazla yazınız`;
    }
    return 'Sonuç yok';
  }, [searchFromMCAPI, internalSearchQuery]);

  return (
    <>
      <Select
        ref={ref}
        isMulti={isMulti}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        components={{
          Option: CustomOption,
          MenuList,
        }}
        maxMenuHeight={300}
        noOptionsMessage={() => optionsMessage}
        loadingMessage={() => 'Yükleniyor...'}
        filterOption={(option: any, inputValue) => {
          if (option.data?.noAccent) {
            let input = inputValue;
            input = input.replace(/^\s+|\s+$/g, '');
            input = stripDiacritics(input);
            input = input.toLowerCase();

            return option.data.noAccent.indexOf(input) > -1;
          }
          return true;
        }}
        placeholder="Seçiniz"
        onInputChange={handleInputChange}
        {...selectProps}
        onChange={handleInternalChange}
        options={tradeMarksOptions?.sort((a: any, b: any) => a.label.length - b.label.length)}
        isLoading={loading}
        isDisabled={disabled}
        // Yalnızca value kullan; defaultValue ile birlikte reset/temizleme güvenilir çalışmaz.
        value={selectedValues}
        menuPortalTarget={document.body}
        styles={{
          option: (provided: any, state: any) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#009EF7' : provided.backgroundColor,
            color: state.isSelected ? 'white' : 'black',
            cursor: 'pointer',
          }),
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          control: (baseStyles, state: any) => ({
            ...baseStyles,
            ...(selectProps?.styles?.control && selectProps?.styles?.control(baseStyles, state)),
            width: '100%',
          }),
          valueContainer: (base) => ({
            ...base,
            ...(selectProps?.styles?.valueContainer && selectProps?.styles?.valueContainer(base, {} as any)),
          }),
        }}
      />
      {error && <div className="invalid-feedback d-block">{error}</div>}
      <FetchErrorModal show={getTradeMarks.isError || getSelectedBrands.isError} chooseOneDefinedError={getTradeMarks.error || getSelectedBrands.error} />
    </>
  );
});

TradeMarksField.displayName = 'TradeMarksField';

export default TradeMarksField;
